/**
 * 模組頁面代碼 : 1340-UI-02
 * 頁面說明 : 帳號管理新增修改視窗
 * @module web/modules/role-permission/components/UserModal
 */

// ----- import from npm modules -----
import React, {useState, useEffect} from 'react';
import CryptoJS from 'crypto-js';

// ----- import from local modules -----
import PasswordInput from '../../../components/PasswordInput';
import {showMessage} from 'web/utils/modalHelper';
import enumList from 'web/utils/enumList';
import UserSelect from './UserSelect';

// ----- constants -----
const USER_NEW = 'new';
const USER_EDIT = 'edit';

const RESPONSE_OK = 200;
const TITLE_USER_NEW = '新增帳號';
const TITLE_USER_EDIT = '修改帳號';

const FIXED_SALT = process.env.REACT_APP_FIXED_SALT;

const UserModal = React.forwardRef((props, ref) => {
  const {
    mode,
    roleSelection,
    positionSelection,
    user,
    searchUser,
    employeeSelection,
  } = props;

  const modalIcon = mode === USER_NEW ? '#ico-plus' : '#ico-edit';
  const modalTitleText = mode === USER_NEW ? TITLE_USER_NEW : TITLE_USER_EDIT;

  // 初始化state
  const [formValues, setFormValues] = useState({
    modalUserAccount: '',
    modalUserRole: '',
    modalUserPsw: '',
    modalUserPswConfirm: '',
    modalUserName: '',
    modalUserPosition: '',
    modalUserMemo: '',
    modalUserEmail: '',
    modalUserEmployee: '',
  });

  const [isAccountDisabled, setIsAccountDisabled] = useState(false); // 初始化帳號欄位是否Disable
  const [isPasswordDisabled, setIsPasswordDisabled] = useState(false); // 初始化密碼輸入區塊是否顯示
  const [invalidShow, setInvalidShow] = useState('none');

  // Handle change event
  const handleChange = (event) => {
    const target = event.target;
    let value = target.value;

    if (target.type === 'select-multiple') {
      value = target.selectedOptions
        ? Array.from(target.selectedOptions, (option) => option.value)
        : [];
    }

    setFormValues({
      ...formValues,
      [target.name]: value,
    });
  };

  const onClose = () => {
    setFormValues({
      modalUserAccount: '',
      modalUserRole: '',
      modalUserPsw: '',
      modalUserPswConfirm: '',
      modalUserName: '',
      modalUserPosition: '',
      modalUserMemo: '',
      modalUserEmail: '',
      modalUserEmployee: '',
    });

    setInvalidShow('none');
    props.clearSelectedEmployee();
  };

  // 使用useEffect在user改變才更新formValues
  useEffect(() => {
    setFormValues({
      modalUserAccount: user ? user.username : '',
      modalUserRole: user ? user.roleId : '',
      modalUserPsw: '',
      modalUserPswConfirm: '',
      modalUserName: user ? user.name : '',
      modalUserPosition: user ? user.positionId : '',
      modalUserMemo: user ? user.memo : '',
      modalUserEmail: user ? user.email : '',
      modalUserEmployee: user ? user.employeeId : '',
    });
    const prepareEmployeeList = async () => {
      await props.fetchAvailableEmployeeList();
      // 這裡的 employeeSelect 是取還沒綁定帳號的員工，編輯時將原先設定的加入選項內
      props.editEmployeeList({
        employeeId: user?.employeeId,
        employeeName: user?.employeeName,
      });
    };
    prepareEmployeeList();

    // 如果修改模式，則設定帳號及密碼欄位要Disable
    setIsAccountDisabled(mode === USER_EDIT ? true : false);
    setIsPasswordDisabled(mode === USER_EDIT ? true : false);
    setInvalidShow('none');
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;

    // 檢查表單是否驗證通過
    if (form.checkValidity() === false) {
      setInvalidShow('block');
      return;
    }
    form.classList.add('was-validated');

    // 建立 FormData 讀取表单的值
    const formData = new FormData(form);

    // 將密碼欄位的值加密
    const psw = formData.get('modalUserPsw');
    if (psw) {
      const encryptedPsw = CryptoJS.SHA256(psw + FIXED_SALT).toString();
      formData.set('modalUserPsw', encryptedPsw);
    }

    // 建立 user 物件
    const saveUser = {
      userId: mode === USER_NEW ? null : user.userId,
      //   account: formData.get('modalUserAccount'),
      //   username: formData.get('modalUserAccount'),
      username: formValues.modalUserAccount,
      roleId: parseInt(formData.get('modalUserRole')),
      password: formData.get('modalUserPsw'),
      email: formData.get('modalUserEmail'),
      name: formData.get('modalUserName'),
      employeeId: formData.get('modalUserEmployee'),
      userName: formData.get('modalUserName'),
      positionId: parseInt(formData.get('modalUserPosition')),
      memo: formData.get('modalUserMemo'),
    };

    // 呼叫父層元件的新增或修改角色函式
    if (mode === USER_NEW) {
      // 调用 props.addUser 方法
      props.addUser(saveUser).then((response) => {
        const statusCode = response.payload.code;
        if (statusCode !== RESPONSE_OK) {
          // showMessage(
          //   enumList.messageModalMode.info,
          //   TITLE_USER_NEW,
          //   response.payload.message,
          // );
          $(ref.current).modal('hide');
          onClose();
          props.onErrorHandle(TITLE_USER_NEW, response.payload);
        } else {
          // 调用 fetchUserList and Hide the modal
          const {username, roleId} = searchUser;
          setTimeout(() => {
            props.fetchUserList(username, roleId).then(() => {
              onClose();
              props.setNeedAccountManagementLoad(true);
              $(ref.current).modal('hide');
            });
          }, 1000); // 1 second delay
        }
      });
    } else if (mode === USER_EDIT) {
      // 調用 props.editUser 方法
      props.editUser(saveUser).then((response) => {
        const statusCode = response.payload.code;
        if (statusCode !== RESPONSE_OK) {
          $(ref.current).modal('hide');
          onClose();
          props.onErrorHandle(TITLE_USER_EDIT, response.payload);
        } else {
          // 調用 fetchUserList and Hide the modal
          const {username, roleId} = searchUser;
          setTimeout(() => {
            props.fetchUserList(username, roleId).then(() => {
              onClose();
              props.setNeedAccountManagementLoad(true);
              $(ref.current).modal('hide');
            });
          }, 1000); // 1 second delay
        }
      });
    }
    form.classList.remove('was-validated');
  };

  return (
    <div
      ref={ref}
      className="modal fade"
      id="modalUser"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
      aria-labelledby="modalAccountLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <form
          className="modal__form modal-content needs-validation"
          id="modalAccountForm"
          noValidate
          onSubmit={handleSubmit}
          autoComplete="false"
        >
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="modalAccountLabel">
              <div className="modal__title__text">{modalTitleText}</div>
            </h1>
            <button
              type="button"
              className="btn-close btn-cancel"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column gap-3 px-2">
              <div className="modal__form__catagory row">
                <div className="col-12">
                  <div className="row">
                    <div className="col-6">
                      <label htmlFor="modalUserAccount" className="form-label">
                        帳號*
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="modalUserAccount"
                        name="modalUserAccount"
                        value={formValues.modalUserAccount}
                        onChange={handleChange}
                        disabled={isAccountDisabled}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="modalUserName" className="form-label">
                        姓名*
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="modalUserName"
                        name="modalUserName"
                        value={formValues.modalUserName || ''}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <label htmlFor="modalUserRole" className="form-label">
                        角色*
                      </label>
                      <select
                        className="form-select form-select-sm"
                        id="modalUserRole"
                        name="modalUserRole"
                        value={formValues.modalUserRole || ''}
                        onChange={handleChange}
                        required
                      >
                        <option disabled value="">
                          請選擇
                        </option>
                        {roleSelection.map((role, index) => (
                          <option key={index} value={role.roleId}>
                            {role.roleName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label htmlFor="modalUserEmail" className="form-label">
                        email
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        id="modalUserEmail"
                        name="modalUserEmail"
                        value={formValues.modalUserEmail || ''}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-12 my-2">
                      <div className="d-flex mb-2">
                        <label
                          htmlFor="modalUserEmployee"
                          className="form-label"
                        >
                          員工
                        </label>
                      </div>
                      <UserSelect
                        name="modalUserEmployee"
                        options={employeeSelection}
                        optionValue="employeeId"
                        labelKey="employeeName"
                        value={formValues.modalUserEmployee}
                        onClear={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            modalUserEmployee: '',
                          }))
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  {!isPasswordDisabled && (
                    <div className="modal__form__catagory row">
                      <div className="col-12">
                        <div className="row">
                          <div className="col-6">
                            <label
                              htmlFor="modalUserPsw"
                              className="form-label"
                            >
                              密碼*
                            </label>
                            <PasswordInput
                              id="modalUserPsw"
                              name="modalUserPsw"
                              value={formValues.modalUserPsw || ''}
                              onChange={handleChange}
                              validateType="password"
                              placeholder=""
                              defaultMassage="密碼長度為8-16碼，至少包含1個以上大寫英文、小寫英文、數字、符號"
                              required
                            />
                          </div>
                          <div className="col-6">
                            <label
                              htmlFor="modalUserPswConfirm"
                              className="form-label"
                            >
                              確認密碼*
                            </label>
                            <PasswordInput
                              id="modalUserPswConfirm"
                              name="modalUserPswConfirm"
                              value={formValues.modalUserPswConfirm || ''}
                              confirmValue={formValues.modalUserPsw}
                              onChange={handleChange}
                              validateType="confirmPassword"
                              placeholder=""
                              defaultMassage="請確認與輸入密碼相符"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="col-12">
                    <label htmlFor="modalUserNote" className="form-label">
                      備註
                    </label>
                    <textarea
                      className="form-control"
                      id="modalUserMemo"
                      name="modalUserMemo"
                      value={formValues.modalUserMemo}
                      onChange={handleChange}
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div
                    className="alert alert-warning global-invalid-feedback"
                    style={{display: invalidShow}}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-cancel"
              data-bs-dismiss="modal"
              onClick={onClose}
            >
              取消
            </button>
            <button type="submit" className="btn btn-primary">
              儲存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default UserModal;
